import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui-components/Card";
import { createTenant } from "./platformApi";
import { deriveNames, GCP_PROJECT } from "./deriveNames";

// These mirror internal/tenant's regexes exactly (internal/tenant/names.go
// :31, :36, :58, :62). They are an affordance -- the server validates
// independently and is the authority -- but keeping them identical means a
// form that passes here does not then 422.
const TENANT_ID_RE = /^[a-z][a-z0-9-]{1,38}[a-z0-9]$/;
const DATASET_RE = /^[a-z][a-z0-9_]{2,40}$/;
const BUCKET_RE = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;
const SERVICE_ACCOUNT_RE =
	/^[a-z][a-z0-9-]{4,28}[a-z0-9]@[a-z][a-z0-9-]{4,28}[a-z0-9]\.iam\.gserviceaccount\.com$/;
const CONNECTION_RE = /^[a-z][a-z0-9-]{1,61}[a-z0-9]$/;

export const FIELDS = [
	{
		name: "id",
		label: "Tenant id",
		hint: "Becomes the Keycloak realm and the subdomain. Lowercase letters, digits and hyphens.",
		placeholder: "acme-bv",
	},
	{ name: "displayName", label: "Display name", hint: "Shown throughout the product.", placeholder: "Acme BV" },
	{
		name: "bqDataset",
		label: "BigQuery dataset",
		hint: "Underscores, never hyphens — BigQuery's rule, not ours.",
		placeholder: "acme_bv",
	},
	{
		name: "gcsBucket",
		label: "GCS bucket",
		hint: "Required: a tenant without its own bucket falls back to the platform bucket.",
		placeholder: "iv-acme-bv",
	},
	{
		name: "bqServiceAccount",
		label: "Service account",
		hint: "The identity the API impersonates for this tenant.",
		placeholder: "tenant-acme@instantviewai-375314.iam.gserviceaccount.com",
	},
	{
		name: "bqConnection",
		label: "BigQuery connection",
		hint:
			"Short id only — project and region are added at use time. Created later, by Provision data access; " +
			"until then the tenant reads through the platform connection.",
		placeholder: "tenant-acme-psql",
	},
];

export function validateCreateForm(values) {
	const errors = {};
	const required = (name, message) => {
		if (!String(values[name] || "").trim()) errors[name] = message;
	};

	required("id", "A tenant id is required");
	required("displayName", "A display name is required");
	required("bqDataset", "A dataset is required");
	required("gcsBucket", "A bucket is required");
	required("bqServiceAccount", "A service account is required");
	// bqConnection is deliberately NOT required: the connection does not exist
	// until Provision data access creates it, and a tenant with an empty one
	// falls back to the platform connection, which is what every tenant does
	// today. Validated only when the operator typed something.

	if (!errors.id && !TENANT_ID_RE.test(values.id)) {
		errors.id = "Lowercase letters, digits and hyphens; start with a letter, 3–40 characters";
	}
	if (!errors.bqDataset && !DATASET_RE.test(values.bqDataset)) {
		errors.bqDataset = "Lowercase letters, digits and underscores; start with a letter";
	}
	if (!errors.gcsBucket && !BUCKET_RE.test(values.gcsBucket)) {
		errors.gcsBucket = "Lowercase letters, digits and hyphens; no dots or underscores";
	}
	if (!errors.bqServiceAccount && !SERVICE_ACCOUNT_RE.test(values.bqServiceAccount)) {
		errors.bqServiceAccount = "Must be a service-account email ending in .iam.gserviceaccount.com";
	}
	if (values.bqConnection && !CONNECTION_RE.test(values.bqConnection)) {
		errors.bqConnection = "Lowercase letters, digits and hyphens; no underscores or dots";
	}
	return errors;
}

const EMPTY = {
	id: "",
	displayName: "",
	bqDataset: "",
	gcsBucket: "",
	bqServiceAccount: "",
	bqConnection: "",
};

// The four resource fields the tenant id prefills, paired with the key
// deriveNames returns for each.
const DERIVED = [
	["bqDataset", "dataset"],
	["gcsBucket", "bucket"],
	["bqServiceAccount", "serviceAccount"],
	["bqConnection", "connection"],
];

export default function TenantCreatePage() {
	const navigate = useNavigate();
	const [values, setValues] = useState(EMPTY);
	const [errors, setErrors] = useState({});
	// Fields the operator has typed into. A derived name is only ever
	// written into a field that is still untouched, so a deliberate override
	// survives the next keystroke in the tenant id.
	const [touched, setTouched] = useState({});
	const [submitError, setSubmitError] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const onChange = (name, value) => {
		setValues((v) => {
			const next = { ...v, [name]: value };
			if (name !== "id") return next;
			const derived = deriveNames(value, GCP_PROJECT);
			for (const [field, key] of DERIVED) {
				if (!touched[field]) next[field] = value ? derived[key] : "";
			}
			return next;
		});
		if (name !== "id") setTouched((t) => ({ ...t, [name]: true }));
	};

	const onSubmit = async (e) => {
		e.preventDefault();
		setSubmitError(null);

		const found = validateCreateForm(values);
		setErrors(found);
		if (Object.keys(found).length > 0) return;

		setSubmitting(true);
		try {
			// Exactly the six fields. The server forces status to
			// provisioning and never reads one from the body.
			const created = await createTenant({ ...values });
			navigate(`/tenants/${created.id}`);
		} catch (err) {
			if (err.field) setErrors((prev) => ({ ...prev, [err.field]: err.message }));
			setSubmitError(err.message);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="max-w-2xl">
			<Card>
				<form onSubmit={onSubmit} noValidate>
					<h1 className="mb-1 text-[15px] font-semibold">Create tenant</h1>
					<p className="mb-5 text-[13px] text-text-2">
						The record is created as <strong>provisioning</strong> and is not served until you activate it.
						The four resource names are derived from the tenant id and stay editable. Provisioning the
						GCP resources is the next step, from the tenant&apos;s page.
					</p>

					{FIELDS.map((f) => (
						<div key={f.name} className="mb-4">
							<label htmlFor={f.name} className="mb-1 block text-[13px] font-medium">
								{f.label}
							</label>
							{/* Width is inline: theme.width is overridden with a tiny custom
							    scale, so w-full is a no-op in this codebase. */}
							<input
								id={f.name}
								name={f.name}
								value={values[f.name]}
								placeholder={f.placeholder}
								onChange={(e) => onChange(f.name, e.target.value)}
								style={{ width: "100%" }}
								className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-3 py-2 font-mono text-[13px] focus:outline-none focus:ring-1 focus:ring-primary"
							/>
							<p className="mt-1 text-[12px] text-text-3">{f.hint}</p>
							{errors[f.name] && (
								<p role="alert" className="mt-1 text-[12px] text-danger">
									{errors[f.name]}
								</p>
							)}
						</div>
					))}

					{submitError && (
						<p role="alert" className="mb-3 text-[13px] text-danger">
							{submitError}
						</p>
					)}

					<div className="flex gap-2">
						<button
							type="submit"
							disabled={submitting}
							className="box-border rounded-ctrl border border-solid border-transparent bg-primary px-3 py-1.5 text-[13px] font-medium text-white disabled:opacity-50"
						>
							{submitting ? "Creating…" : "Create tenant"}
						</button>
						<button
							type="button"
							onClick={() => navigate("/tenants")}
							className="box-border rounded-ctrl border border-solid border-card-bd px-3 py-1.5 text-[13px]"
						>
							Cancel
						</button>
					</div>
				</form>
			</Card>
		</div>
	);
}
